import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  useMediaQuery,
  Container,
} from "@mui/material";
import {
  Menu as MenuIcon,
  Person,
  Description,
  ContactSupport,
  Close,
  Work,
} from "@mui/icons-material";
import { Link, useLocation } from "react-router-dom";
import styled from "styled-components";
import { navigationRoutes } from "../../routes";
import logo from "../../resources/logo.png";

const navigationIcons = {
  About: <Person />,
  Projects: <Work />,
  Resume: <Description />,
  Contact: <ContactSupport />,
};

const StyledAppBar = styled(AppBar)`
  && {
    background: rgba(255, 255, 255, 0.88);
    border-bottom: 1px solid rgba(0, 102, 204, 0.14);
    box-shadow: 0 18px 45px rgba(0, 24, 64, 0.08);
    backdrop-filter: blur(18px);
  }
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  cursor: pointer;
  text-decoration: none;
  transition: transform 0.3s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const NavButton = styled(Button)`
  && {
    border: 1px solid transparent;
    border-radius: 6px;
    color: #075ba9;
    font-size: 0.94rem;
    font-weight: 700;
    min-height: 40px;
    min-width: 88px;
    padding: 0 16px;
    position: relative;
    text-transform: none;
    transition:
      background-color 180ms ease,
      border-color 180ms ease,
      box-shadow 180ms ease,
      color 180ms ease;
  }

  &&:hover {
    background: rgba(0, 102, 204, 0.1);
    border-color: rgba(0, 102, 204, 0.12);
    color: #004f9e;
  }

  &&.active {
    background: linear-gradient(135deg, #0066cc, #0089f0);
    border-color: rgba(0, 102, 204, 0.42);
    box-shadow:
      0 8px 20px rgba(0, 102, 204, 0.22),
      inset 0 1px 0 rgba(255, 255, 255, 0.26);
    color: white;
  }

  &&:focus-visible {
    outline: 3px solid rgba(0, 137, 240, 0.34);
    outline-offset: 2px;
  }

  @media (max-width: 1120px) {
    && {
      font-size: 0.86rem;
      min-width: 74px;
      padding: 0 11px;
    }
  }
`;

const MobileMenuButton = styled(IconButton)`
  display: none;

  @media (max-width: 960px) {
    && {
      align-items: center;
      background: #0066cc;
      border: 1px solid #0056ad;
      border-radius: 8px;
      box-shadow: 0 6px 16px rgba(0, 70, 145, 0.22);
      color: #ffffff;
      display: flex;
      height: 44px;
      justify-content: center;
      width: 44px;
    }

    &&:hover {
      background: #0057ad;
    }

    &&:focus-visible {
      outline: 3px solid rgba(0, 137, 240, 0.34);
      outline-offset: 2px;
    }
  }
`;

const DesktopNav = styled(Box)`
  align-items: center;
  background: rgba(0, 102, 204, 0.055);
  border: 1px solid rgba(0, 102, 204, 0.12);
  border-radius: 8px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
  display: flex;
  gap: 4px;
  padding: 4px;
  align-items: center;

  @media (max-width: 960px) {
    display: none;
  }

  @media (max-width: 1120px) {
    gap: 2px;
  }
`;

const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
`;

const DrawerTitle = styled(Typography)`
  color: #0066cc;
  font-weight: 600;
`;

const MobileNavItem = styled(ListItem)`
  padding: 1rem 1.5rem;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(0, 102, 204, 0.1);
  }

  &.active {
    background: #0066cc;
    color: white;
  }
`;

function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const isMobile = useMediaQuery("(max-width: 960px)");
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen((isOpen) => !isOpen);
  };

  const handleNavClick = () => {
    setMobileOpen(false);
  };

  const isActive = (path) => location.pathname === path;

  const drawer = (
    <Box sx={{ width: 280 }}>
      <DrawerHeader>
        <DrawerTitle variant="h6">Menu</DrawerTitle>
        <IconButton onClick={handleDrawerToggle}>
          <Close />
        </IconButton>
      </DrawerHeader>
      <List>
        {navigationRoutes.map((item) => (
          <MobileNavItem
            key={item.path}
            component={Link}
            to={item.path}
            onClick={handleNavClick}
            className={isActive(item.path) ? "active" : ""}
            sx={{
              color: isActive(item.path) ? "white" : "inherit",
              "& .MuiListItemIcon-root": {
                color: isActive(item.path) ? "white" : "inherit",
              },
            }}
          >
            <ListItemIcon>{navigationIcons[item.label]}</ListItemIcon>
            <ListItemText primary={item.label} />
          </MobileNavItem>
        ))}
      </List>
    </Box>
  );

  return (
    <>
      <StyledAppBar position="fixed" elevation={0}>
        <Container maxWidth="xl">
          <Toolbar sx={{ padding: "0.5rem 0" }}>
            <LogoContainer to="/">
              <img
                src={logo}
                alt="CS Development Logo"
                style={{
                  height: "2.5rem",
                  marginRight: "0.5rem",
                }}
              />
              <Typography
                variant="h6"
                component="div"
                sx={{
                  color: "#0066CC",
                  fontWeight: 700,
                  display: { xs: "none", sm: "block" },
                  fontFamily: "DotGothic16",
                }}
              >
                CS Development
              </Typography>
            </LogoContainer>

            <Box sx={{ flexGrow: 1 }} />

            <DesktopNav>
              {navigationRoutes.map((item) => (
                <NavButton
                  key={item.path}
                  component={Link}
                  to={item.path}
                  className={isActive(item.path) ? "active" : ""}
                  aria-current={isActive(item.path) ? "page" : undefined}
                >
                  {item.label}
                </NavButton>
              ))}
            </DesktopNav>

            {isMobile && (
              <MobileMenuButton
                aria-label="Open navigation menu"
                edge="end"
                onClick={handleDrawerToggle}
              >
                <MenuIcon sx={{ fontSize: 28 }} />
              </MobileMenuButton>
            )}
          </Toolbar>
        </Container>
      </StyledAppBar>

      <Drawer
        variant="temporary"
        anchor="right"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: 280,
            borderLeft: "1px solid rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}

export default Header;
